import { useState, useEffect } from 'react'
import { Users, Plus, Settings, Mail, Trash2, UserPlus, Crown, Shield } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { handleApiError } from '@/lib/utils'
import CreateTeamModal from '@/components/teams/CreateTeamModal'
import TeamInviteModal from '@/components/teams/TeamInviteModal'
import TeamDetailsModal from '@/components/teams/TeamDetailsModal'

interface Team {
  id: number
  name: string
  description: string
  team_type: string
  max_members: number
  member_count: number
  is_active: boolean
  created_at: string
  team_lead: {
    id: number
    name: string
    email: string
  } | null
  members: Array<{
    name: string
    role: string
  }>
}

export default function TeamsPage() {
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)

  useEffect(() => {
    loadTeams()
  }, [])

  const loadTeams = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await apiClient.getTeams()
      if (response.success) {
        setTeams((response.data as any) || [])
      } else {
        setError('Failed to load teams')
      }
    } catch (err) {
      setError('Failed to load teams: ' + handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleTeamCreated = () => {
    setShowCreateModal(false)
    loadTeams() // Refresh the list
  }

  const handleInviteSent = () => {
    setShowInviteModal(false)
    // Optionally show success message
  }

  const handleViewTeam = (team: Team) => {
    setSelectedTeam(team)
    setShowDetailsModal(true)
  }

  const handleInviteToTeam = (team: Team) => {
    setSelectedTeam(team)
    setShowInviteModal(true)
  }

  const getTeamTypeColor = (type: string) => {
    const colors = {
      recruiting: 'bg-blue-100 text-blue-800',
      hiring: 'bg-green-100 text-green-800',
      hr: 'bg-purple-100 text-purple-800',
      operations: 'bg-orange-100 text-orange-800',
      other: 'bg-gray-100 text-gray-800'
    }
    return colors[type as keyof typeof colors] || colors.other
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'lead': return <Crown className="w-4 h-4 text-yellow-600" />
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />
      default: return <Users className="w-4 h-4 text-gray-500" />
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading teams...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="md:flex md:items-center md:justify-between mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
              Teams
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              manage your recruiting teams and invite members
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={() => setShowCreateModal(true)}
              className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Team
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Teams Grid */}
        {teams.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No teams</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first recruiting team.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {teams.map((team) => (
              <div
                key={team.id}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewTeam(team)}
              >
                <div className="p-6">
                  {/* Team header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <Users className="w-8 h-8 text-gray-400" />
                      <div className="ml-3">
                        <h3 className="text-lg font-medium text-gray-900">
                          {team.name}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTeamTypeColor(team.team_type)}`}>
                          {team.team_type}
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleInviteToTeam(team)
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="Invite to team"
                      >
                        <Mail className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Team description */}
                  <p className="text-sm text-gray-600 mb-4">
                    {team.description || 'No description provided'}
                  </p>

                  {/* Team stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center">
                      <UserPlus className="w-4 h-4 mr-1" />
                      <span>{team.member_count} / {team.max_members} members</span>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-1 ${team.is_active ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                      <span>{team.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  </div>

                  {/* Team lead */}
                  {team.team_lead && (
                    <div className="flex items-center text-sm text-gray-600 mb-4">
                      <Crown className="w-4 h-4 mr-2 text-yellow-600" />
                      <span>Led by {team.team_lead.name}</span>
                    </div>
                  )}

                  {/* Team members preview */}
                  {team.members.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                        Members
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {team.members.slice(0, 3).map((member, index) => (
                          <div
                            key={index}
                            className="flex items-center text-xs bg-gray-100 rounded-full px-2 py-1"
                          >
                            {getRoleIcon(member.role)}
                            <span className="ml-1">{member.name}</span>
                          </div>
                        ))}
                        {team.members.length > 3 && (
                          <div className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-1">
                            +{team.members.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        <CreateTeamModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTeamCreated={handleTeamCreated}
        />

        <TeamInviteModal
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          team={selectedTeam}
          onInviteSent={handleInviteSent}
        />

        <TeamDetailsModal
          open={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          team={selectedTeam}
          onTeamUpdated={loadTeams}
        />
      </div>
    </div>
  )
}